/**
 * A contact's priority as a badge: the name, a strength icon, and one colour in three weights.
 *
 * High is solid, Medium is tinted, Low is an outline. It is one hue on purpose. Red and green would
 * say "bad" and "good", and a Low priority contact is neither. The icon says the same thing as the
 * colour, so the rank does not depend on telling shades apart.
 */
import { Minus, SignalHigh, SignalLow, SignalMedium, type LucideIcon } from 'lucide-react';
import { Badge } from '@design-system';
import type { Prospecting } from '@client/api/types';
import { TIER_LABELS } from '../lib/labels';

const STYLE: Record<Prospecting.Tier, { variant: 'default' | 'outline'; className?: string; Icon: LucideIcon }> = {
  tier1: { variant: 'default', className: 'border-transparent bg-primary text-white', Icon: SignalHigh },
  tier2: { variant: 'default', Icon: SignalMedium },
  tier3: { variant: 'outline', className: 'border-gray-300 text-gray-700', Icon: SignalLow },
  untiered: { variant: 'outline', className: 'border-gray-200 text-gray-500', Icon: Minus },
};

interface PriorityBadgeProps {
  tier: Prospecting.Tier;
}

export function PriorityBadge({ tier }: PriorityBadgeProps) {
  const { variant, className, Icon } = STYLE[tier];
  return (
    <Badge
      variant={variant}
      className={className}
    >
      <Icon aria-hidden="true" />
      {TIER_LABELS[tier]}
    </Badge>
  );
}
